import React, { ReactNode, useMemo } from "react";

import { faLock } from "@fortawesome/pro-solid-svg-icons/faLock";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IntlShape, useIntl } from "react-intl";

import Visibility from "@mapmycustomers/shared/enum/Visibility";
import Team from "@mapmycustomers/shared/types/Team";
import { SelectField, SelectFieldProps } from "@mapmycustomers/ui";

import itemToValueLabel from "./utils/itemToValueLabel";
import styles from "./VisibilitySelectField.module.scss";

const sameNode = (node: ReactNode) => node;

interface VisibilitySelectFieldProps<T extends Visibility> extends Pick<SelectFieldProps, "size"> {
  className?: string;
  disabled?: boolean;
  // The popup renders in a portal, so styling its options takes a class on the popup itself — a
  // descendant selector from `className` never reaches it.
  dropdownClassName?: string;
  label?: ReactNode;
  noteGetter?: (intl: IntlShape, visibility: T) => string;
  onChange?: (visibility: T) => void;
  onTeamIdsChange?: (teamIds: Team["id"][]) => void;
  options?: { disabled?: boolean; label: string; value: T }[];
  teamIds?: Team["id"][];
  teams: Team[];
  teamsError?: ReactNode;
  teamsRenderer?: (node: ReactNode) => ReactNode;
  value?: T;
  visibilityRenderer?: (node: ReactNode) => ReactNode;
}

const VisibilitySelectField = <T extends Visibility>({
  className,
  disabled,
  dropdownClassName,
  label,
  noteGetter,
  onChange,
  onTeamIdsChange,
  options,
  size = "large",
  teamIds,
  teams,
  teamsError,
  teamsRenderer = sameNode,
  value,
  visibilityRenderer = sameNode,
}: VisibilitySelectFieldProps<T>) => {
  const intl = useIntl();
  const teamOptions = useMemo(
    () =>
      teams
        .filter((team) => team.viaTeamAccess || teamIds?.includes(team.id))
        .map(itemToValueLabel),
    [teams, teamIds]
  );

  const note = value ? (noteGetter?.(intl, value) ?? "") : "";

  return (
    <div className={className}>
      <div className={styles.inputGroup}>
        {visibilityRenderer(
          <SelectField<T>
            disabled={disabled}
            dropdownClassName={dropdownClassName}
            dropdownMatchSelectWidth={false}
            label={label}
            onChange={onChange}
            options={options}
            size={size}
            suffixIcon={disabled ? <FontAwesomeIcon icon={faLock} /> : undefined}
            value={value}
          />
        )}
        {value === Visibility.SHARED_WITH_TEAM &&
          teamsRenderer(
            <SelectField<Team["id"][]>
              disabled={disabled}
              dropdownMatchSelectWidth={false}
              error={teamsError}
              label={intl.formatMessage({
                id: "visibilitySelectField.teams.label",
                defaultMessage: "Teams",
                description: "Label for teams select field in visibility select field",
              })}
              mode="multiple"
              onChange={onTeamIdsChange}
              options={teamOptions}
              required
              showSearch
              size={size}
              value={teamIds}
            />
          )}
      </div>
      {note && <div className={styles.note}>{note}</div>}
    </div>
  );
};

export default VisibilitySelectField;
